import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { AiOutlineClose } from 'react-icons/ai';
import { HiOutlineLightBulb } from 'react-icons/hi2';
import {
  ProfileSettingsModal,
  LocationAutocompleteInputField,
  FieldSelectModern,
  Form,
  ToggleSwitch,
} from '../../components';
import { Form as FinalForm } from 'react-final-form';
import css from './ProfileSettingsBasicInfoForm.css';

const identity = v => v;

const HOURLY_FEE_OPTIONS = [
  { value: '20-39 / h', label: '20-39 €' },
  { value: '40-59 / h', label: '40-59 €' },
  { value: '60-79 / h', label: '60-79 €' },
  { value: '80-99 / h', label: '80-99 €' },
  { value: '+ 100 / h', label: '100+ €' },
];

const getLanguageTranslationId = langKey => {
  if (!langKey) return null;
  const capitalized = langKey.charAt(0).toUpperCase() + langKey.slice(1);
  return `SearchLanguages.language${capitalized}`;
};

// Bridges a Final Form field value back into external React state.
// react-final-form's <Field> does not invoke a custom `onChange` prop, so the
// only reliable way to observe value changes (e.g. from the location
// autocomplete) is to read the value from the render prop and sync it here.
export const FormValueSync = ({ value, onChange }) => {
  useEffect(() => {
    onChange(value);
  }, [value, onChange]);
  return null;
};

FormValueSync.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
};

const ProfileSettingsBasicInfoForm = props => {
  const {
    isOpen,
    onClose,
    onManageDisableScrolling,
    onSubmit,
    location,
    languages,
    hourlyFee,
    dontShowHourlyFee,
    languageOptions,
    updateInProgress,
    hideHourlyFee,
    intl,
  } = props;

  const [locationValue, setLocationValue] = useState(location || null);
  const [selectedLanguages, setSelectedLanguages] = useState(languages || []);
  const [hourlyFeeValue, setHourlyFeeValue] = useState(hourlyFee || '');
  const [dontShowHourlyFeeValue, setDontShowHourlyFeeValue] = useState(
    dontShowHourlyFee != null ? dontShowHourlyFee : false
  );

  // Reset values when the modal opens. Only on open: the page passes a freshly
  // mapped languages array on every render (resize, any Redux update), and
  // resetting then would drop the unsaved changes.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setLocationValue(location || null);
      setSelectedLanguages(languages || []);
      setHourlyFeeValue(hourlyFee || '');
      setDontShowHourlyFeeValue(dontShowHourlyFee != null ? dontShowHourlyFee : false);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, location, languages, hourlyFee, dontShowHourlyFee]);

  const isDirty =
    JSON.stringify(locationValue) !== JSON.stringify(location || null) ||
    JSON.stringify(selectedLanguages) !== JSON.stringify(languages || []) ||
    hourlyFeeValue !== (hourlyFee || '') ||
    dontShowHourlyFeeValue !== (dontShowHourlyFee != null ? dontShowHourlyFee : false);

  const handleSave = () => {
    onSubmit({
      location: locationValue,
      languages: selectedLanguages,
      hourlyFee: hourlyFeeValue,
      dontShowHourlyFee: dontShowHourlyFeeValue,
    });
    onClose();
  };

  // Language helpers
  const getLabel = key => {
    const id = getLanguageTranslationId(key);
    return id ? intl.formatMessage({ id }) : key;
  };

  const languageSelectOptions = languageOptions
    ? languageOptions
        .filter(opt => !selectedLanguages.includes(opt.key))
        .map(opt => ({ value: opt.key, label: getLabel(opt.key) }))
    : [];

  const handleLanguageSelect = option => {
    if (!option) return;
    setSelectedLanguages([...selectedLanguages, option.value]);
  };

  const removeLanguage = langKey => {
    setSelectedLanguages(selectedLanguages.filter(l => l !== langKey));
  };

  // Hourly fee select value
  const hourlyFeeSelectValue = hourlyFeeValue
    ? HOURLY_FEE_OPTIONS.find(o => o.value === hourlyFeeValue) || null
    : null;

  const hourlyRatePlaceholder = intl.formatMessage({
    id: 'ProfileSettingsPage.hourlyRatePlaceholder',
  });

  return (
    <ProfileSettingsModal
      id="basicInfo"
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      title={intl.formatMessage({ id: 'ProfileSettingsPage.editBasicInfoTitle' })}
      // subtitle={intl.formatMessage({ id: 'ProfileSettingsPage.editBasicInfoSubtitle' })}
      onSubmit={handleSave}
      submitDisabled={!isDirty}
      submitInProgress={updateInProgress}
      submitLabel={intl.formatMessage({ id: 'ProfileSettingsPage.saveButton' })}
    >
      <FinalForm
        onSubmit={() => {}}
        initialValues={{
          location: locationValue,
          languageSearch: null,
          hourlyFee: hourlyFeeSelectValue,
        }}
        render={({ values, form }) => (
          <Form className={css.formContent} onSubmit={() => {}}>
            {/* Keep external state in sync with the Final Form location value */}
            <FormValueSync value={values.location} onChange={setLocationValue} />
            {/* Location */}
            <div className={css.fieldGroup}>
              <label className={css.label}>
                {intl.formatMessage({ id: 'ProfileSettingsPage.locationLabel' })}
              </label>
              <div className={css.locationWrapper}>
                <LocationAutocompleteInputField
                  name="location"
                  placeholder={intl.formatMessage({ id: 'ProfileSettingsPage.locationLabel' })}
                  format={identity}
                  valueFromForm={values.location}
                />
              </div>
            </div>

            {/* Languages */}
            <div>
              <FieldSelectModern
                name="languageSearch"
                id="languageSearch"
                label={intl.formatMessage({ id: 'ProfileSettingsPage.languagesLabel' })}
                options={languageSelectOptions}
                isSearchable={true}
                isClearable={false}
                onChange={option => {
                  if (option) {
                    handleLanguageSelect(option);
                    form.change('languageSearch', null);
                  }
                }}
              />
              {selectedLanguages.length > 0 ? (
                <div className={css.languageTags}>
                  {selectedLanguages.map(lang => (
                    <span key={lang} className={css.languageTag}>
                      {getLabel(lang)}
                      <button
                        type="button"
                        className={css.languageTagRemove}
                        onClick={() => removeLanguage(lang)}
                      >
                        <AiOutlineClose className={css.languageTagRemoveIcon} />
                      </button>
                    </span>
                  ))}
                </div>
              ) : null}
            </div>

            {/* Hourly rate */}
            {!hideHourlyFee ? (
              <div>
                <FieldSelectModern
                  name="hourlyFee"
                  id="hourlyFee"
                  label={intl.formatMessage({ id: 'ProfileSettingsPage.hourlyRateLabel' })}
                  options={HOURLY_FEE_OPTIONS}
                  isSearchable={false}
                  isClearable={false}
                  placeholder={hourlyRatePlaceholder}
                  onChange={option => {
                    setHourlyFeeValue(option ? option.value : '');
                    form.change('hourlyFee', option);
                  }}
                />
                <ToggleSwitch
                  isOn={dontShowHourlyFeeValue}
                  onToggle={setDontShowHourlyFeeValue}
                  defaultState={false}
                  label={intl.formatMessage({ id: 'ProfileSettingsPage.hideHourlyFeeLabel' })}
                />
                <div className={css.hourlyFeeInfoBox}>
                  <div className={css.hourlyFeeInfoHeader}>
                    <span className={css.hourlyFeeInfoIconCircle}>
                      <HiOutlineLightBulb className={css.hourlyFeeInfoIcon} />
                    </span>
                    <span className={css.hourlyFeeInfoTitle}>
                      {intl.formatMessage({ id: 'ProfileSettingsPage.hourlyFeeInfoTitle' })}
                    </span>
                  </div>
                  <p className={css.hourlyFeeInfoText}>
                    {intl.formatMessage({ id: 'ProfileSettingsPage.hourlyFeeInfoText' })}
                  </p>
                </div>
              </div>
            ) : null}
          </Form>
        )}
      />
    </ProfileSettingsModal>
  );
};

ProfileSettingsBasicInfoForm.defaultProps = {
  location: null,
  languages: [],
  hourlyFee: '',
  dontShowHourlyFee: undefined,
  languageOptions: [],
  updateInProgress: false,
  hideHourlyFee: false,
};

ProfileSettingsBasicInfoForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onManageDisableScrolling: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  location: PropTypes.object,
  languages: PropTypes.array,
  hourlyFee: PropTypes.string,
  dontShowHourlyFee: PropTypes.bool,
  languageOptions: PropTypes.array,
  updateInProgress: PropTypes.bool,
  hideHourlyFee: PropTypes.bool,
  intl: intlShape.isRequired,
};

export default injectIntl(ProfileSettingsBasicInfoForm);
